/**
 * Downloads a QR code SVG as a high-quality PNG image
 * @param svgElement - The SVG element to convert
 * @param filename - The desired filename (without extension)
 * @param size - Output size in pixels (default 512x512 for print quality)
 */
export const downloadQRCodeAsPNG = async (
  svgElement: SVGElement,
  filename: string,
  size: number = 512
): Promise<void> => {
  try {
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Get SVG data
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // Create an image from the SVG
    const img = new Image();
    
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });

    // Draw white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, size, size);

    // Draw the image onto the canvas
    ctx.drawImage(img, 0, 0, size, size);

    // Clean up the object URL
    URL.revokeObjectURL(url);

    // Convert canvas to blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/png');
    });

    // Create download link
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${sanitizeFilename(filename)}-showcase-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading QR code:', error);
    throw error;
  }
};

/**
 * Sanitizes a filename by removing special characters
 */
const sanitizeFilename = (filename: string): string => {
  return filename
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};
